import React, { useState, useEffect } from 'react';
import {
  Settings,
  DollarSign,
  TrendingUp,
  Shield,
  Mail,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Bell,
  Users,
  Lock,
  Server,
  Activity
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const PlatformControls = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('system');

  // States for each configuration type
  const [systemConfig, setSystemConfig] = useState(null);
  const [feeConfig, setFeeConfig] = useState(null);
  const [tradingLimits, setTradingLimits] = useState(null);
  const [kycThresholds, setKycThresholds] = useState(null);
  const [notificationConfig, setNotificationConfig] = useState(null);

  const BACKEND_URL = '';

  useEffect(() => {
    fetchAllConfigs();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchAllConfigs = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();

      // Fetch all configurations
      const [system, fees, limits, kyc, notifications] = await Promise.all([
        fetch(`${BACKEND_URL}/api/platform/config/system`, { headers }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/platform/config/fees`, { headers }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/platform/config/trading-limits`, { headers }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/platform/config/kyc-thresholds`, { headers }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/platform/config/notifications`, { headers }).then(r => r.json())
      ]);

      setSystemConfig(system.config);
      setFeeConfig(fees.fees);
      setTradingLimits(limits.limits);
      setKycThresholds(kyc.kyc);
      setNotificationConfig(notifications.notifications);

      toast({
        title: 'Success',
        description: 'Platform configurations loaded'
      });
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load configurations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // System Configuration Updates
  const updateMaintenanceMode = async (enabled, message) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/maintenance`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled, message })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: `Maintenance mode ${enabled ? 'enabled' : 'disabled'}`
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update maintenance mode',
        variant: 'destructive'
      });
    }
  };

  const updateFeatureFlag = async (featureName, enabled, description = null) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/feature-flag`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ feature_name: featureName, enabled, description })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: `Feature ${featureName} ${enabled ? 'enabled' : 'disabled'}`
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update feature flag',
        variant: 'destructive'
      });
    }
  };

  // Fee Management Updates
  const updateTradingFees = async (makerFee, takerFee, vipDiscount) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/trading-fees`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          maker_fee: parseFloat(makerFee),
          taker_fee: parseFloat(takerFee),
          vip_discount: parseFloat(vipDiscount)
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Trading fees updated successfully'
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update trading fees',
        variant: 'destructive'
      });
    }
  };

  const updateWithdrawalFees = async (currency, feePercentage, minFee, maxFee) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/withdrawal-fees`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          currency,
          fee_percentage: parseFloat(feePercentage),
          min_fee: parseFloat(minFee),
          max_fee: parseFloat(maxFee)
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: `Withdrawal fees for ${currency} updated`
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update withdrawal fees',
        variant: 'destructive'
      });
    }
  };

  // Trading Limits Updates
  const updateTradingLimits = async (pair, minOrder, maxOrder, maxLeverage, enabled) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/trading-limits`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          pair,
          min_order_size: parseFloat(minOrder),
          max_order_size: parseFloat(maxOrder),
          max_leverage: parseInt(maxLeverage),
          enabled
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: `Trading limits for ${pair} updated`
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update trading limits',
        variant: 'destructive'
      });
    }
  };

  // KYC Thresholds Updates
  const updateKYCThresholds = async (dailyLimit, monthlyLimit, tradeLimit, requiresKYC) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/kyc-thresholds`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          daily_withdrawal_limit: parseFloat(dailyLimit),
          monthly_withdrawal_limit: parseFloat(monthlyLimit),
          single_trade_limit: parseFloat(tradeLimit),
          requires_kyc: requiresKYC
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: 'KYC thresholds updated successfully'
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update KYC thresholds',
        variant: 'destructive'
      });
    }
  };

  // Notification Settings Updates
  const updateNotificationSettings = async (emailEnabled, pushEnabled, smsEnabled, inAppEnabled) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/notification-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email_enabled: emailEnabled,
          push_enabled: pushEnabled,
          sms_enabled: smsEnabled,
          in_app_enabled: inAppEnabled
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Notification settings updated'
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update notification settings',
        variant: 'destructive'
      });
    }
  };

  const updateEmailTemplate = async (templateName, subject, body, variables) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform/config/email-template`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          template_name: templateName,
          subject,
          body,
          variables
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: `Email template '${templateName}' updated`
        });
        fetchAllConfigs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update email template',
        variant: 'destructive'
      });
    }
  };

  const ToggleSwitch = ({ enabled, onChange, label, description }) => (
    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-cyan-500/20 hover:border-cyan-500/40 transition-all">
      <div>
        <span className="text-white font-semibold block">{label}</span>
        {description && <span className="text-cyan-400 text-sm">{description}</span>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-14 h-7 rounded-full transition-colors ${
          enabled ? 'bg-green-500' : 'bg-gray-600'
        }`}
      >
        <div
          className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-lg transition-transform ${
            enabled ? 'translate-x-8' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const TabButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
        activeTab === id
          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
          : 'bg-slate-800/50 text-cyan-400 hover:bg-slate-700'
      }`}
    >
      <Icon className="w-5 h-5" />
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-400 font-semibold">Loading Platform Controls...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Server className="w-8 h-8 text-cyan-400" />
            Platform Controls
          </h1>
          <p className="text-cyan-400">Complete platform configuration and management</p>
        </div>
        <button
          onClick={fetchAllConfigs}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh All
        </button>
      </div>

      {/* Warning Banner */}
      <div className="bg-orange-900/20 border border-orange-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-orange-400 font-semibold mb-1">⚠️ Mock Email Service Active</h3>
            <p className="text-orange-300 text-sm">
              Email notifications are currently mocked for testing. Emails will be logged to console instead of being sent. 
              Configure real email service (Mailgun/SendGrid) for production use.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-3">
        <TabButton id="system" label="System Config" icon={Settings} />
        <TabButton id="fees" label="Fee Management" icon={DollarSign} />
        <TabButton id="trading" label="Trading Controls" icon={TrendingUp} />
        <TabButton id="kyc" label="KYC Thresholds" icon={Shield} />
        <TabButton id="notifications" label="Notifications" icon={Mail} />
      </div>

      {/* System Configuration Tab */}
      {activeTab === 'system' && systemConfig && (
        <div className="space-y-6">
          {/* Maintenance Mode */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-red-400" />
              Maintenance Mode
            </h2>
            <ToggleSwitch
              enabled={systemConfig.maintenance_mode?.enabled || false}
              onChange={(val) => updateMaintenanceMode(val, systemConfig.maintenance_mode?.message)}
              label="Enable Maintenance Mode"
              description="When enabled, users will see a maintenance message"
            />
            <div className="mt-4">
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Maintenance Message
              </label>
              <textarea
                value={systemConfig.maintenance_mode?.message || ''}
                onChange={(e) => setSystemConfig({
                  ...systemConfig,
                  maintenance_mode: { ...systemConfig.maintenance_mode, message: e.target.value }
                })}
                rows={3}
                className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              />
              <button
                onClick={() => updateMaintenanceMode(
                  systemConfig.maintenance_mode?.enabled,
                  systemConfig.maintenance_mode?.message
                )}
                className="mt-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
              >
                Update Message
              </button>
            </div>
          </div>

          {/* Feature Flags */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-cyan-400" />
              Feature Flags
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {systemConfig.feature_flags && Object.entries(systemConfig.feature_flags).map(([key, value]) => (
                <ToggleSwitch
                  key={key}
                  enabled={value}
                  onChange={(val) => updateFeatureFlag(key, val)}
                  label={key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  description={`Enable/disable ${key} feature`}
                />
              ))}
            </div>
          </div>

          {/* API Limits */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">API Rate Limits</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Requests/Minute
                </label>
                <input
                  type="number"
                  value={systemConfig.api_limits?.requests_per_minute || 60}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Requests/Hour
                </label>
                <input
                  type="number"
                  value={systemConfig.api_limits?.requests_per_hour || 1000}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Requests/Day
                </label>
                <input
                  type="number"
                  value={systemConfig.api_limits?.requests_per_day || 10000}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fee Management Tab */}
      {activeTab === 'fees' && feeConfig && (
        <div className="space-y-6">
          {/* Trading Fees */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Trading Fees
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Maker Fee (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={feeConfig.trading_fees?.maker_fee || 0}
                  onChange={(e) => setFeeConfig({
                    ...feeConfig,
                    trading_fees: { ...feeConfig.trading_fees, maker_fee: parseFloat(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Taker Fee (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={feeConfig.trading_fees?.taker_fee || 0}
                  onChange={(e) => setFeeConfig({
                    ...feeConfig,
                    trading_fees: { ...feeConfig.trading_fees, taker_fee: parseFloat(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  VIP Discount (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={feeConfig.trading_fees?.vip_discount || 0}
                  onChange={(e) => setFeeConfig({
                    ...feeConfig,
                    trading_fees: { ...feeConfig.trading_fees, vip_discount: parseFloat(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>
            </div>
            <button
              onClick={() => updateTradingFees(
                feeConfig.trading_fees?.maker_fee,
                feeConfig.trading_fees?.taker_fee,
                feeConfig.trading_fees?.vip_discount
              )}
              className="mt-4 flex items-center gap-2 px-6 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold"
            >
              <Save className="w-4 h-4" />
              Save Trading Fees
            </button>
          </div>

          {/* Withdrawal Fees */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">Withdrawal Fees by Currency</h2>
            <div className="space-y-4">
              {feeConfig.withdrawal_fees && Object.entries(feeConfig.withdrawal_fees).map(([currency, fees]) => (
                <div key={currency} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Currency</label>
                    <input
                      type="text"
                      value={currency}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white font-bold"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Fee (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={fees.fee_percentage}
                      onChange={(e) => {
                        const updated = { ...feeConfig.withdrawal_fees };
                        updated[currency].fee_percentage = parseFloat(e.target.value);
                        setFeeConfig({ ...feeConfig, withdrawal_fees: updated });
                      }}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Min Fee</label>
                    <input
                      type="number"
                      step="0.0001"
                      value={fees.min_fee}
                      onChange={(e) => {
                        const updated = { ...feeConfig.withdrawal_fees };
                        updated[currency].min_fee = parseFloat(e.target.value);
                        setFeeConfig({ ...feeConfig, withdrawal_fees: updated });
                      }}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Max Fee</label>
                    <input
                      type="number"
                      step="0.01"
                      value={fees.max_fee}
                      onChange={(e) => {
                        const updated = { ...feeConfig.withdrawal_fees };
                        updated[currency].max_fee = parseFloat(e.target.value);
                        setFeeConfig({ ...feeConfig, withdrawal_fees: updated });
                      }}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                    />
                  </div>
                  <button
                    onClick={() => updateWithdrawalFees(
                      currency,
                      fees.fee_percentage,
                      fees.min_fee,
                      fees.max_fee
                    )}
                    className="md:col-span-4 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
                  >
                    Update {currency} Fees
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trading Controls Tab */}
      {activeTab === 'trading' && tradingLimits && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Trading Pair Limits
            </h2>
            <div className="space-y-4">
              {tradingLimits.pairs && Object.entries(tradingLimits.pairs).map(([pair, limits]) => (
                <div key={pair} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Trading Pair</label>
                    <input
                      type="text"
                      value={pair}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white font-bold"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Min Order ($)</label>
                    <input
                      type="number"
                      value={limits.min_order}
                      onChange={(e) => {
                        const updated = { ...tradingLimits.pairs };
                        updated[pair].min_order = parseFloat(e.target.value);
                        setTradingLimits({ ...tradingLimits, pairs: updated });
                      }}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Max Order ($)</label>
                    <input
                      type="number"
                      value={limits.max_order}
                      onChange={(e) => {
                        const updated = { ...tradingLimits.pairs };
                        updated[pair].max_order = parseFloat(e.target.value);
                        setTradingLimits({ ...tradingLimits, pairs: updated });
                      }}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Max Leverage</label>
                    <input
                      type="number"
                      value={limits.max_leverage}
                      onChange={(e) => {
                        const updated = { ...tradingLimits.pairs };
                        updated[pair].max_leverage = parseInt(e.target.value);
                        setTradingLimits({ ...tradingLimits, pairs: updated });
                      }}
                      className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-cyan-400 text-sm font-semibold mb-2">Status</label>
                    <button
                      onClick={() => {
                        const updated = { ...tradingLimits.pairs };
                        updated[pair].enabled = !updated[pair].enabled;
                        setTradingLimits({ ...tradingLimits, pairs: updated });
                      }}
                      className={`w-full px-4 py-2 rounded-lg font-semibold ${
                        limits.enabled
                          ? 'bg-green-500 text-white'
                          : 'bg-red-500 text-white'
                      }`}
                    >
                      {limits.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                  <button
                    onClick={() => updateTradingLimits(
                      pair,
                      limits.min_order,
                      limits.max_order,
                      limits.max_leverage,
                      limits.enabled
                    )}
                    className="md:col-span-5 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
                  >
                    Update {pair} Settings
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* KYC Thresholds Tab */}
      {activeTab === 'kyc' && kycThresholds && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              KYC Verification Thresholds
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Daily Withdrawal Limit ($)
                </label>
                <input
                  type="number"
                  value={kycThresholds.thresholds?.daily_withdrawal_limit || 0}
                  onChange={(e) => setKycThresholds({
                    ...kycThresholds,
                    thresholds: { ...kycThresholds.thresholds, daily_withdrawal_limit: parseFloat(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Monthly Withdrawal Limit ($)
                </label>
                <input
                  type="number"
                  value={kycThresholds.thresholds?.monthly_withdrawal_limit || 0}
                  onChange={(e) => setKycThresholds({
                    ...kycThresholds,
                    thresholds: { ...kycThresholds.thresholds, monthly_withdrawal_limit: parseFloat(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Single Trade Limit ($)
                </label>
                <input
                  type="number"
                  value={kycThresholds.thresholds?.single_trade_limit || 0}
                  onChange={(e) => setKycThresholds({
                    ...kycThresholds,
                    thresholds: { ...kycThresholds.thresholds, single_trade_limit: parseFloat(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                />
              </div>
            </div>
            <div className="mt-4">
              <ToggleSwitch
                enabled={kycThresholds.thresholds?.requires_kyc || false}
                onChange={(val) => setKycThresholds({
                  ...kycThresholds,
                  thresholds: { ...kycThresholds.thresholds, requires_kyc: val }
                })}
                label="Require KYC Verification"
                description="Enforce KYC verification for withdrawals above limits"
              />
            </div>
            <button
              onClick={() => updateKYCThresholds(
                kycThresholds.thresholds?.daily_withdrawal_limit,
                kycThresholds.thresholds?.monthly_withdrawal_limit,
                kycThresholds.thresholds?.single_trade_limit,
                kycThresholds.thresholds?.requires_kyc
              )}
              className="mt-4 flex items-center gap-2 px-6 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold"
            >
              <Save className="w-4 h-4" />
              Save KYC Thresholds
            </button>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && notificationConfig && (
        <div className="space-y-6">
          {/* Notification Channels */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-yellow-400" />
              Notification Channels
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ToggleSwitch
                enabled={notificationConfig.channels?.email_enabled || false}
                onChange={(val) => {
                  const updated = { ...notificationConfig };
                  updated.channels.email_enabled = val;
                  setNotificationConfig(updated);
                  updateNotificationSettings(
                    val,
                    updated.channels.push_enabled,
                    updated.channels.sms_enabled,
                    updated.channels.in_app_enabled
                  );
                }}
                label="Email Notifications"
                description="Send notifications via email (mocked)"
              />
              <ToggleSwitch
                enabled={notificationConfig.channels?.push_enabled || false}
                onChange={(val) => {
                  const updated = { ...notificationConfig };
                  updated.channels.push_enabled = val;
                  setNotificationConfig(updated);
                  updateNotificationSettings(
                    updated.channels.email_enabled,
                    val,
                    updated.channels.sms_enabled,
                    updated.channels.in_app_enabled
                  );
                }}
                label="Push Notifications"
                description="Send push notifications to mobile apps"
              />
              <ToggleSwitch
                enabled={notificationConfig.channels?.sms_enabled || false}
                onChange={(val) => {
                  const updated = { ...notificationConfig };
                  updated.channels.sms_enabled = val;
                  setNotificationConfig(updated);
                  updateNotificationSettings(
                    updated.channels.email_enabled,
                    updated.channels.push_enabled,
                    val,
                    updated.channels.in_app_enabled
                  );
                }}
                label="SMS Notifications"
                description="Send notifications via SMS"
              />
              <ToggleSwitch
                enabled={notificationConfig.channels?.in_app_enabled || false}
                onChange={(val) => {
                  const updated = { ...notificationConfig };
                  updated.channels.in_app_enabled = val;
                  setNotificationConfig(updated);
                  updateNotificationSettings(
                    updated.channels.email_enabled,
                    updated.channels.push_enabled,
                    updated.channels.sms_enabled,
                    val
                  );
                }}
                label="In-App Notifications"
                description="Display notifications within the app"
              />
            </div>
          </div>

          {/* Email Templates */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Email Templates (Mocked)
            </h2>
            <div className="space-y-4">
              {notificationConfig.email_templates && Object.entries(notificationConfig.email_templates).map(([name, template]) => (
                <div key={name} className="p-4 bg-slate-800/50 rounded-lg border border-cyan-500/20">
                  <h3 className="text-white font-semibold mb-2 capitalize">{name.replace(/_/g, ' ')}</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-cyan-400 text-sm font-semibold mb-1">Subject</label>
                      <input
                        type="text"
                        value={template.subject}
                        className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-cyan-400 text-sm font-semibold mb-1">Body</label>
                      <textarea
                        value={template.body}
                        rows={3}
                        className="w-full px-4 py-2 bg-slate-700 border border-cyan-500/30 rounded-lg text-white"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-cyan-400 text-sm font-semibold mb-1">Variables</label>
                      <div className="flex flex-wrap gap-2">
                        {template.variables.map(variable => (
                          <span key={variable} className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-mono">
                            {'{' + variable + '}'}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <p className="text-blue-300 text-sm">
                <strong>Note:</strong> Email templates are currently read-only with mock email service. 
                Configure real email service to enable template editing and actual email delivery.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformControls;
